import { create } from "zustand";

/**
 * Shared Zustand store driving the global bubble tooltip overlay.
 *
 * Why a store instead of per-bubble local state: in the vanilla
 * (`_legacy/xaes-fx.js`) every bubble shared a single `#bubble-tooltip`
 * DOM node — only one tooltip is visible at a time and it tracks the
 * cursor as the user moves between bubbles. A store keeps that
 * "singleton overlay" model intact.
 *
 * `accent` is a CSS colour string (any value valid in a `color:` or
 * `border-color:` declaration — including `var(--accent-sky)`). The
 * overlay uses it for the border, title, and accent rail so the
 * tooltip's chrome picks up the hovered bubble's stroke colour.
 *
 * `badge` + `subtitle` are optional richer fields used by the HAW
 * bubble tooltip in `_legacy/wd.html` (`#haw-bubble-tooltip`,
 * lines 2567-2641). When present, the overlay renders them above /
 * below the title in vanilla's badge → title → subtitle → description
 * stack. MC-style bubbles leave them undefined and get the original
 * two-line layout.
 *
 * Position is stored in **client pixels** (clientX / clientY) so the
 * overlay can place itself with plain `position: fixed`.
 */

export type BubbleTooltipPayload = {
  title: string;
  /** Plain string = one paragraph; string[] = bullet list. */
  body: string | string[];
  x: number;
  y: number;
  accent?: string;
  /** Small uppercase eyebrow above the title (e.g. "MODE 1"). */
  badge?: string;
  /** Italics-leaning secondary line between title and body. */
  subtitle?: string;
};

type BubbleTooltipState = {
  active: BubbleTooltipPayload | null;
  show: (payload: BubbleTooltipPayload) => void;
  hide: () => void;
};

export const useBubbleTooltip = create<BubbleTooltipState>((set) => ({
  active: null,
  show: (payload) => set({ active: payload }),
  hide: () => set({ active: null }),
}));
